import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const HomeStats = ({ stats, i18n }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const lang = i18n && i18n.language === "ar" ? "ar" : "en";
  const norm = useMemo(() => {
    if (!stats) return { title: "", items: [] };
    if (Array.isArray(stats)) {
      const s0 = stats[0] || {};
      const title =
        lang === "ar"
          ? s0.lang?.title || s0.title_ar || s0.titleAr || s0.title || s0.title_en || ""
          : s0.lang?.title || s0.title_en || s0.title || s0.titleAr || s0.title_ar || "";
      const items = (s0.items || []).map((it, i) => ({
        id: it.id ?? i,
        number: String(it.number || "0"),
        color: it.color || "#0071BD",
        label:
          lang === "ar"
            ? it.lang?.label || it.label_ar || it.labelAr || it.label_en || it.label || ""
            : it.lang?.label || it.label_en || it.label || it.labelAr || it.label_ar || "",
      }));
      return { title, items };
    }
    const title =
      lang === "ar"
        ? stats.titleAr || stats.title_ar || stats.lang?.title || stats.title || ""
        : stats.title || stats.title_en || stats.lang?.title || stats.titleAr || stats.title_ar || "";
    const items = (stats.items || []).map((it, i) => ({
      id: it.id ?? i,
      number: String(it.number || "0"),
      color: it.color || "#0071BD",
      label: lang === "ar" ? it.labelAr || it.label_ar || it.label || "" : it.label || it.label_en || it.labelAr || it.label_ar || "",
    }));
    return { title, items };
  }, [stats, lang]);
  const [animatedNumbers, setAnimatedNumbers] = useState(norm.items.map(() => 0));

  useEffect(() => {
    setAnimatedNumbers(norm.items.map(() => 0));
  }, [norm.items.length]);

  useEffect(() => {
    if (inView) {
      norm.items.forEach((item, i) => {
        let start = 0;
        const match = String(item.number).match(/\d+/);
        const target = match ? parseInt(match[0], 10) : 0;
        if (target <= 0) {
          setAnimatedNumbers((prev) => {
            const updated = [...prev];
            updated[i] = 0;
            return updated;
          });
          return;
        }
        const duration = 1500;
        const stepTime = Math.max(10, Math.floor(duration / target));
        const timer = setInterval(() => {
          start += 1;
          setAnimatedNumbers((prev) => {
            const updated = [...prev];
            updated[i] = start;
            return updated;
          });
          if (start >= target) clearInterval(timer);
        }, stepTime);
      });
    }
  }, [inView, norm.items]);

  return (
    <section className="py-5 text-center bg-light" id="stats" ref={ref}>
      <div className="container">
        <h2 className="fw-bold mb-4" style={{ color: "#0071BD" }}>
          {norm.title}
        </h2>

        <div className="row justify-content-center g-4">
          {norm.items.map((item, i) => (
            <motion.div
              key={item.id}
              className="col-6 col-md-3"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
            >
              <h1 style={{ color: item.color, fontWeight: "700" }}>
                {animatedNumbers[i]}
                {String(item.number).includes("+") && "+"}
              </h1>
              <p className="text-secondary">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeStats;
